import styles from "./ShortInterestPostionsChart.module.scss";
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import Skeleton from "../skeleton/Skeleton";
import { negativeState, positiveState } from "@/lib/store";
import { Stock } from "@/models/stock";
import { getStockShortInterestPositions } from "@/controllers/stock-shorts";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

type Props = {
    stock: Stock;
    isExpanded: boolean;
};

const findSeries = (data, name: string) => data.find((d) => d.measure === name);

/** Daily changes in reported short positions as columns (up in green, down in red), the aggregated position as a line. */
const ShortInterestPositionsChart = ({ stock }: Props) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const [loading, setLoading] = useState(true);
    const [changes, setChanges] = useState<[number, number][]>([]);
    const [aggregated, setAggregated] = useState<[number, number][]>([]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getStockShortInterestPositions(stock.tradingitem_id)
            .then((data) => {
                if (!alive) return;
                setAggregated(findSeries(data, "aggregated_position")?.data ?? []);
                setChanges(findSeries(data, "change_pc")?.data ?? []);
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [stock.stock_id]);

    const series = useMemo<TimeSeries[]>(
        () => [
            { id: "changes", title: "Change", type: "column", data: changes, colorBySign: true, upColor: positive, downColor: negative, axis: "left", precision: 1, format: "compact" },
            { id: "aggregated_changes", title: "Aggregated Changes", type: "line", data: aggregated, color: "#2196f3", axis: "right", precision: 1, format: "compact" },
        ],
        [changes, aggregated, positive, negative]
    );

    return loading ? (
        <Skeleton variant="rounded" width="100%" height="100%" loading={loading} />
    ) : (
        <div style={{ height: "100%" }}>
            <div className={styles.chart}>
                <TimeSeriesChart
                    series={series}
                    initialRange="All"
                    rangeButtons={[
                        { label: "1m", days: 31 },
                        { label: "3m", days: 92 },
                        { label: "6m", days: 183 },
                        { label: "1y", days: 366 },
                        { label: "All", days: 0 },
                    ]}
                    exportName={`${stock.ticker}-positions`}
                />
            </div>
        </div>
    );
};

export default ShortInterestPositionsChart;
